import React, {useCallback, useEffect, useState} from 'react';
import {CircularProgress, IconButton, InputAdornment, Link, Typography} from '@mui/material';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import axios from 'axios';
import {getCookie, setCookie} from '../../utils/cookies';
import {accessTokenKey, authRedirectReason, refreshTokenKey, usernameKey} from '../../core/AuthInterceptor';
import {useNavigate} from 'react-router-dom';
import {
    ErrorText,
    ForgotPasswordWrapper,
    FormWrapper,
    LoginCard,
    RegisterLink,
    StyledButton,
    StyledTextField
} from './Login.styled';
import TAILogo from '../../assets/images/taia_logo.png';
import {AuthContainer} from "./Auth.styled";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {usePageTitle} from '../../hooks/usePageTitle';
import {PageTitleKeys} from '../../config/routeConstants';
import LogoWithLanguageSwitcher from '../../components/common/LogoWithLanguageSwitcher';
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';

const Login: React.FC = () => {
    usePageTitle(PageTitleKeys.Login);
    const {t} = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isFetchingToken, setIsFetchingToken] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    useEffect(() => {
        const redirectReason = localStorage.getItem(authRedirectReason);
        if (redirectReason === 'unauthorized') {
            showSnackbar(t('login.relogin'), 'warning');
            localStorage.removeItem(authRedirectReason);
        }

        const hasToken = !!getCookie(accessTokenKey);
        if (hasToken) {
            navigate('/chat', {replace: true});
        }
    }, [navigate, showSnackbar, t]);

    const validatePassword = (value: string) => {
        if (value.length < 10) {
            return t('passwordValidation.minLength');
        }
        if (!/[A-Z]/.test(value)) {
            return t('passwordValidation.uppercase');
        }
        if (!/[a-z]/.test(value)) {
            return t('passwordValidation.lowercase');
        }
        if (!/\d/.test(value)) {
            return t('passwordValidation.digit');
        }
        if (!/[!@#$%^&()_+{};':"\\|,.<>/?[\]]/.test(value)) {
            return t('passwordValidation.specialChar');
        }
        return "";
    };

    const handleLogin = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        if (passwordError) return;
        setIsFetchingToken(true);
        setLoginError(false);
        try {
            const response = await axios.post(`/auth/login`, {email: username, password});
            if (response.status === 200) {
                const {accessToken, refreshToken, username: returnedUsername} = response.data;
                setCookie(accessTokenKey, accessToken, 30);
                setCookie(refreshTokenKey, refreshToken, 30);
                setCookie(usernameKey, returnedUsername ?? username, 30);
                localStorage.removeItem(authRedirectReason);
            }
            navigate('/chat', {replace: true});
        } catch (error: any) {
            console.log(error);
            if (error?.response?.status === 401 || error?.response?.status === 400) {
                setLoginError(true);
            } else {
                showSnackbar(parseError(error), "error");
            }
        } finally {
            setIsFetchingToken(false);
        }
    }, [username, password, showSnackbar, navigate]);

    const handleFieldChange = () => {
        if (loginError) {
            setLoginError(false);
        }
    };

    const handleRegistration = () => {
        navigate('/register');
    };

    const navigateToResetPassword = () => {
        navigate('/reset-password');
    };

    return (
        <AuthContainer role="region" aria-labelledby="login-title">
            <LogoWithLanguageSwitcher src={TAILogo} alt={t('auth.logo.alt')} clickable={true}/>
            <LoginCard>
                <Typography id="login-title" variant="h5" align="center" width="100%" gutterBottom>
                    {t('login.title')}
                </Typography>
                <Typography variant="body2" align="center" color="textSecondary" marginBottom="16px">
                    {t('login.welcome')}
                </Typography>
                <FormWrapper onSubmit={handleLogin} aria-live="polite" aria-atomic={true} aria-busy={isFetchingToken}>
                    <div>
                        <StyledTextField
                            label={t('login.email')}
                            fullWidth
                            margin="normal"
                            value={username}
                            onChange={(e) => {
                                setUsername(e.target.value);
                                handleFieldChange();
                            }}
                            autoComplete="on"
                            disabled={isFetchingToken}
                            error={loginError}
                            inputProps={{ 'aria-label': t('login.email.ariaLabel') as string }}
                        />
                    </div>
                    <div>
                        <StyledTextField
                            label={t('login.password')}
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                handleFieldChange();
                                setPasswordError("");
                            }}
                            onBlur={() => {
                                const error = validatePassword(password);
                                setPasswordError(error || "");
                            }}
                            autoComplete="on"
                            disabled={isFetchingToken}
                            error={!!passwordError || loginError}
                            helperText={passwordError}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('login.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ 'aria-label': t('login.password.ariaLabel') as string }}
                        />
                        <div style={{height: '24px'}}>
                            {loginError && <ErrorText>{t('login.invalidCredentials')}</ErrorText>}
                        </div>
                    </div>
                    {isFetchingToken && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('login.loading')}
                        </div>
                    )}
                    <ForgotPasswordWrapper>
                        <Link
                            onClick={!isFetchingToken ? navigateToResetPassword : undefined}
                            underline="always"
                            color="primary"
                            sx={{
                                '&:hover': {textDecoration: 'none'},
                                cursor: isFetchingToken ? 'default' : 'pointer',
                                opacity: isFetchingToken ? 0.5 : 1
                            }}
                        >
                            {t('login.forgotPassword')}
                        </Link>
                    </ForgotPasswordWrapper>
                    <StyledButton type="submit" variant="contained"
                                  onClick={handleLogin}
                                  disabled={isFetchingToken || !username || !password || loginError || !!passwordError}>
                        {isFetchingToken ?
                            <CircularProgress size={24} sx={{color: 'inherit'}}/> : t('login.loginButton')}
                    </StyledButton>
                    <RegisterLink
                        onClick={!isFetchingToken ? handleRegistration : undefined}
                        disabled={isFetchingToken}
                    >
                        {t('login.register')}
                    </RegisterLink>
                </FormWrapper>
            </LoginCard>
        </AuthContainer>
    );
};

export default Login;
